import {HttpErrorResponse} from '@angular/common/http';
import {addError$, DvbError, ERROR_CODE, ErrorLevel, ErrorType} from '@dv/shared/code';
import {tapResponse} from '@ngrx/operators';
import type {MonoTypeOperatorFunction} from 'rxjs';

export interface HandleResponseObserver<T> {
    next?: (value: T) => void;
    complete?: () => void;
    finalize?: () => void;
}

/**
 * Shortcut to tapResponse with default error handling.
 * HttpErrorResponse errors are ignored, as they are expected to be handled by a global error hook.
 * Other errors are logged with console.error()
 *
 * This operator catches errors and returns an EMPTY observable in the error case.
 */
export function handleResponse<T>(observer: HandleResponseObserver<T>): MonoTypeOperatorFunction<T> {
    return source$ => source$.pipe(tapResponse<T>({
        next: observer.next ?? (() => {
            // nop
        }),
        error: error => handleResponseError(error),
        complete: observer.complete,
        finalize: observer.finalize,
    }));
}

/**
 * Use this function when you need more control over error handling, e.g. within a catchError block and a custom
 * error response.
 */
export function handleResponseError(error: unknown, consoleLogPrefix?: string): void {
    if (error instanceof HttpErrorResponse) {
        return;
    }

    const dvbError = new DvbError(ErrorType.INTERNAL,
        ErrorLevel.SEVERE,
        'ERRORS.ERR_TECHNICAL',
        ERROR_CODE.WRAPPED_ERROR,
        {error});

    addError$.next(dvbError);

    if (consoleLogPrefix) {
        console.error(consoleLogPrefix, error);
    } else {
        console.error(error);
    }
}
